/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  Box,
  Grid,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomTextField } from '../../index';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContainedButton } from '../../index';

export const FamilyBackgroundTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpRelations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpRelations((prev) => [
      {
        empid: props.empid,
        lastname: '',
        firstname: '',
        middlename: '',
        occupation: '',
        employer: '',
        address: '',
        telephone: '',
        cellphone: '',
        dateofbirth: null,
        relationship: '',
        emergency: false,
        referenceperson: false,
      },
      ...prev,
    ]);
  };

  const handleDelete = (index) => {
    props.setEmpRelations((prev) => prev.filter((_, i) => i !== index));
  };

  const boxStyle = {
    mx: 0,
    my: 1,
    px: 3,
    py: 2,
  };

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle={'Add Relation'}
            style={{ width: 'auto' }}
          />
        )}
      </Box>
      {props.empRelations.map((relation, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}
          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <CustomTextField
                error={!relation.firstname}
                errorMsg="Please enter a first name"
                label="First Name"
                value={relation.firstname || ''}
                onChange={(e) =>
                  handleChange(index, 'firstname', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!relation.lastname}
                errorMsg="Please enter a lastname"
                label="Last Name"
                value={relation.lastname || ''}
                onChange={(e) =>
                  handleChange(index, 'lastname', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a middle name"
                label="Middle Name"
                value={relation.middlename || ''}
                onChange={(e) =>
                  handleChange(index, 'middlename', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!relation.relationship}
                errorMsg="Please enter relationship"
                label="Relationship"
                value={relation.relationship || ''}
                onChange={(e) =>
                  handleChange(index, 'relationship', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter an occupation"
                label="Occupation"
                value={relation.occupation || ''}
                onChange={(e) =>
                  handleChange(index, 'occupation', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter an employer"
                label="Employer"
                value={relation.employer || ''}
                onChange={(e) =>
                  handleChange(index, 'employer', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!relation.address}
                errorMsg="Please enter an address"
                label="Address"
                value={relation.address || ''}
                onChange={(e) => handleChange(index, 'address', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={
                    relation.dateofbirth ? new Date(relation.dateofbirth) : null
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'dateofbirth', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  maxDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!relation.cellphone}
                errorMsg="Please enter a cellphone number"
                label="Cellphone No."
                value={relation.cellphone || ''}
                onChange={(e) =>
                  handleChange(index, 'cellphone', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a telephone number"
                label="Telephone No."
                value={relation.telephone || ''}
                onChange={(e) =>
                  handleChange(index, 'telephone', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={relation.emergency}
                    onChange={
                      !props.viewOnly
                        ? (e) => {
                            const checked = e.target.checked;
                            handleChange(index, 'emergency', checked);
                          }
                        : undefined // read-only in viewOnly mode
                    }
                    disabled={
                      !relation.emergency &&
                      props.empRelations.some(
                        (rel, i) => rel.emergency && i !== index
                      )
                    }
                  />
                }
                label="Emergency Contact"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={relation.referenceperson}
                    onChange={
                      !props.viewOnly
                        ? (e) => {
                            const checked = e.target.checked;
                            handleChange(index, 'referenceperson', checked);
                          }
                        : undefined // read-only in viewOnly mode
                    }
                  />
                }
                label="Reference Person"
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
